import { SlicePipe } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FilterBase } from 'src/app/shared/grid/filter-base';
import { GridComponentService } from 'src/app/shared/grid/grid-component.service';
import { OptionModel } from './option.model';
import { GRID_MATERIAL_PROVIDERS } from 'src/app/shared/grid/grid-material-control-defaults.const';

/**
 * Provides Autocomplete, MultiSelect for very long filters, and Chip display.
 * The user types in a search text and the results from the search text are displayed as options to not impact performance.
 * Finally, the selected options are displayed as chips.
 * 1) This uses MatAutoComplete
 * 2) Sends the results of the search text as Selection options.
 * 3) When the user selects an option, traps the click to prevent the the multi-select from closing
 * 4) Uses Angular Chips to display selected items
 *
 * Link:
 * https://stackoverflow.com/questions/50453564/how-do-i-implement-autocomplete-in-a-mat-select-component
 * https://material.angular.io/components/chips/overview
 */

@UntilDestroy()
@Component({
    selector: 'grid-option-autocomplete-multiselect-filter',
    templateUrl: './grid-option-autocomplete-multiselect-filter.component.html',
    providers: [
        ...GRID_MATERIAL_PROVIDERS,
        { provide: FilterBase, useExisting: GridOptionAutoCompleteMultiselectFilter }
    ],
    styleUrls: ['./grid-option-autocomplete-multiselect-filter-component.scss'],
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatCheckboxModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule,
        ReactiveFormsModule,
        FormsModule,
        SlicePipe,
        MatChipsModule,
        MatButtonModule
    ]
})
export class GridOptionAutoCompleteMultiselectFilter extends FilterBase implements OnInit, OnChanges {
    inputValueControl = new UntypedFormControl('');
    @Input() set selectedItems(items: any[]) {
        this._selectedItems = [...items];
    }

    get selectedItems(): any[] {
        return this._selectedItems;
    }

    listSelected: boolean;
    @Input() maxDataRows = 50;
    @Input() field: string;
    @Input() persistLookup = false;
    @Input() isCheckbox = false;
    @Input() data: any[] = [];
    // Holds the user search text and will alert any subscribers when the text changes
    @Input() searchText: Subject<string>;
    @Input() minInputLength = 4;
    @Input() selectAll = false;
    @Input() showChips = true;
    @Input() uniqueFilterClass = '';
    @Input() onSaveColumns: Observable<void>;
    @Input() onResetColumns: Observable<void>;
    @Input() gridService: GridComponentService;
    @Input() gridName: string;
    @Input() valueField: string = 'id';

    @Output() filterValueChanging = new EventEmitter<FilterDescriptor>();
    @Output() filterValueChanged = new EventEmitter<CompositeFilterDescriptor>();

    @ViewChild('auto') matAuto: MatAutocomplete;
    @ViewChild('searchTextInput') searchTextInput: ElementRef<HTMLInputElement>;
    public filteredOptions: string[] | OptionModel[];
    public height: string;

    private _selectedItems: any[] = [];

    constructor() {
        super();
    }

    ngOnInit(): void {
        // Recompute how big the viewport should be.
        this.height = '200px';
        const value = this.getLatestFilterValue(null, this.field);
        const storedSelectedOptions = this.gridService?.getAsyncFilters(this.gridName, this.field) ?? [];

        if (value && typeof value !== 'object') {
            this.selectedItems.push(value);
        } else if (Array.isArray(value)) {
            for (const val of value) {
                if (typeof val !== 'object' && storedSelectedOptions.length > 0) {
                    const storedOption = storedSelectedOptions.find((x) => x.id === val);
                    this.selectedItems.push(storedOption ?? val);
                } else {
                    this.selectedItems.push(val);
                }
            }
        } else if (value) {
            this.selectedItems.push(value);
        }

        if (this.persistLookup) {
            const storedLookups = this.gridService.replaceLookupValues(this.gridName, this.field, this.selectedItems);
            if (storedLookups.length > 0) {
                this.selectedItems = storedLookups;
            }
        }

        // user enters in search text and then Select options are populated and displayed
        this.inputValueControl.valueChanges
            .pipe(
                // time in MS between key events
                debounceTime(250),
                distinctUntilChanged(),
                untilDestroyed(this)
            )
            .subscribe((inputValue) => {
                if ((inputValue && inputValue.length >= this.minInputLength) || this.minInputLength === 0) {
                    this.searchText.next(inputValue || '');
                }
            });

        if (this.onSaveColumns) {
            this.onSaveColumns.subscribe(() => {
                this.gridService.saveAsyncFiltersForField(this.gridName, this.field, this.selectedItems);
            });
        }

        if (this.onResetColumns) {
            this.onResetColumns.subscribe(() => {
                this.selectedItems = [];
            });
        }
    }

    /**
     * We want to detect when the user clicks on the global "Clear ALL filter" icon.
     * So we need to check these conditions:
     * 1) Changes were in the "filter"
     * 2) The user has filters selected for the column
     * 3) The previous filter has filter values but the current filter does not
     * If these conditions are true, then clear this.selectedItems which is used to display
     * the "chips"
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges) {
        // we want to verify only when filters are changing and this.selectedItems have data
        if (changes.filter && this.selectedItems && this.selectedItems.length > 0) {
            // compare current & prev filters to check for global "Clear All filter" ICON case
            const currentFilter = changes.filter.currentValue;
            const prevFilter = changes.filter.previousValue;
            // previous has filters but current does not so "Clear ALL filter" ICON was clicked
            if (currentFilter.filters.length === 0 && prevFilter.filters.length > 0) {
                this.selectedItems.length = 0;
            }
        }

        if (this.persistLookup) {
            this.gridService.saveLookupsForFilter(this.gridName, this.field, this.valueField, this.selectedItems);
        }
    }

    onFocus(): void {
        if (this.minInputLength === 0) {
            this.searchText.next('');
        }
    }

    clearAll(): void {
        this.selectedItems = [];
        this.applyFilter();
        this.gridService.clearFilterLookup(this.gridName, this.field);
    }

    onRemove(item: any): void {
        this.removeItem(item);
        this.applyFilter();
    }

    removeItem(item: any): void {
        let index: number;
        if (typeof item === 'object') {
            index = (this.selectedItems as OptionModel[]).findIndex((elem) => elem.id === item.id);
        } else {
            index = this.selectedItems.findIndex((value) => value === item);
        }
        if (index >= 0) {
            this.selectedItems.splice(index, 1);
        }
    }

    isSelected(item: any): boolean {
        if (typeof item === 'object') {
            return (this.selectedItems as OptionModel[]).some((elem) => elem.id === item.id);
        } else {
            return this.selectedItems.includes(item);
        }
    }

    optionSelected(item: string | OptionModel): void {
        const SELECTALL = 'Select All';
        const UNSELECTALL = 'Unselect All';
        if ((typeof item === 'object' && item.name === SELECTALL) || (typeof item !== 'object' && item === SELECTALL)) {
            this.selectedItems = this.data;
            return;
        }
        if (
            (typeof item === 'object' && item.name === UNSELECTALL) ||
            (typeof item !== 'object' && item === UNSELECTALL)
        ) {
            this.selectedItems = [];
            return;
        }
        if (typeof item === 'object' && !(this.selectedItems as OptionModel[]).some((elem) => elem.id === item.id)) {
            this.selectedItems.push(item);
        } else if (typeof item !== 'object' && !this.selectedItems.includes(item)) {
            this.selectedItems.push(item);
        } else {
            this.removeItem(item);
        }
    }

    applyFilter(): void {
        this.data = [];
        this.searchTextInput.nativeElement.value = '';
        this.inputValueControl.reset();
        let filterVals: string[] | number[];
        if (this.selectedItems && this.selectedItems.length > 0 && typeof this.selectedItems[0] === 'object') {
            filterVals = (this.selectedItems as OptionModel[]).map((elem) => elem[this.valueField]);
        } else if (this.selectedItems && this.selectedItems.length > 0 && typeof this.selectedItems[0] !== 'object') {
            filterVals = this.selectedItems;
        }

        const filterDescriptor: FilterDescriptor = {
            field: this.field,
            operator: 'eq',
            value: filterVals
        };

        this.filterValueChanging.emit(filterDescriptor);

        this.trimFilterByField(this.filter, this.field);
        this.updateFilter(filterDescriptor);

        this.filterValueChanged.emit(this.filter);
    }
}
